import { Component } from '@theme/component';

// Animate content groups, rather than their interactive descendants or background layers.
const COLUMNS = [
  '.arvana-hero__content', '.arvana-hero__visual',
  '.arvana-page-hero__content', '.arvana-page-hero__media',
  '.arvana-media-text__media', '.arvana-media-text__content',
  '.arvana-product-row__media', '.arvana-product-row__content',
  '.arvana-intro-media__columns > *',
  '.arvana-statement__rule', '.arvana-statement__body',
  '.arvana-tabs__list', '.arvana-tabs__panels',
  '.arvana-feature-grid__column', '.arvana-feature-grid__media',
  '.arvana-testimonials__aside', '.arvana-testimonials__slides',
  '.arvana-story__quote', '.arvana-story__person', '.arvana-story__photo', '.arvana-story__content',
  '.arvana-faq__aside', '.arvana-faq__list',
  '.arvana-contact__form-card', '.arvana-contact__aside',
  '.arvana-banner__row > *',
].join(',');

const CARDS = [
  '.arvana-application', '.arvana-icon-columns__item',
  '.arvana-icon-columns__cell--heading', '.arvana-icon-columns__cell--image',
  '.arvana-icon-grid__item', '.arvana-image-columns__item',
  '.arvana-industry-card', '.arvana-step', '.arvana-product-card',
  '.arvana-compare__item', '.arvana-footer__menu',
].join(',');

const UP = [
  '.arvana-section-heading', '.arvana-hero__logos',
  '.arvana-intro-media__header > .arvana-label', '.arvana-intro-media__media',
  '.arvana-icon-columns__cta', '.arvana-comparison__scroll',
  '.arvana-icon-grid__button', '.arvana-image-columns__button',
  '.arvana-compare__more', '.arvana-industry-grid__controls',
  '.arvana-banner--centered .arvana-banner__inner',
  '.arvana-footer__brand', '.arvana-footer__bottom',
].join(',');

class ArvanaScrollReveal extends Component {
  #seen = new WeakSet();
  #pending = new Set();
  #animations = new Map();
  #observer;
  #mutations;
  #abort;
  #motion;
  #frame = 0;
  #scope;

  connectedCallback() {
    super.connectedCallback();
    this.#scope = this.parentElement;
    if (!this.#scope || !('IntersectionObserver' in window) ||
        !Element.prototype.animate || !CSS.supports('translate', '0px')) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motion.addEventListener('change', this.#syncMotion, { signal });
    this.#scope.addEventListener('focusin', this.#interaction, { signal });
    this.#scope.addEventListener('pointerdown', this.#interaction, { signal, capture: true });
    window.addEventListener('hashchange', this.#anchor, { signal });
    document.addEventListener('shopify:section:load', this.#scheduleScan, { signal });
    document.addEventListener('shopify:block:select', this.#interaction, { signal });
    this.#syncMotion();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleScan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#stop();
  }

  #syncMotion = () => {
    this.#stop();
    if (this.#motion.matches || !this.isConnected) return;
    this.#observer = new IntersectionObserver(this.#enter, {
      rootMargin: '0px 0px -8% 0px', threshold: 0,
    });
    this.#mutations = new MutationObserver(this.#scheduleScan);
    this.#mutations.observe(this.#scope, { childList: true, subtree: true });
    this.#scan();
    this.#anchor();
  };

  #stop() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#mutations?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    for (const element of [...this.#pending, ...this.#animations.keys()]) this.#finish(element);
    this.#pending.clear();
  }

  #scheduleScan = () => {
    if (!this.#observer || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#scan();
    });
  };

  #scan() {
    // Prune removed section nodes so editor re-renders do not retain detached content.
    for (const element of [...this.#pending, ...this.#animations.keys()]) {
      if (!this.#scope.contains(element)) this.#finish(element);
    }
    const sections = this.#scope.querySelectorAll(
      'main .shopify-section[class*="arvana-"], footer .shopify-section[class*="arvana-"]'
    );
    for (const section of sections) {
      if (section.matches('.arvana-schedule-call-section, .arvana-topbar-section')) continue;
      const groups = new Map();
      for (const element of section.querySelectorAll(COLUMNS)) groups.set(element, 'side');
      for (const element of section.querySelectorAll(CARDS)) groups.set(element, 'card');
      for (const element of section.querySelectorAll(UP)) {
        if (!groups.has(element)) groups.set(element, 'up');
      }
      // Keep the outer group when a section heading is inside a column or card.
      for (const [element, kind] of groups) {
        if ([...groups.keys()].some((parent) => parent !== element && parent.contains(element))) continue;
        if (this.#seen.has(element) || this.#pending.has(element)) continue;
        element.dataset.arvanaRevealKind = kind;
        const rect = element.getBoundingClientRect();
        const siblings = [...element.parentElement.children].filter((child) => child.matches(CARDS));
        const row = siblings.filter((child) => Math.abs(child.getBoundingClientRect().top - rect.top) < 4);
        element.dataset.arvanaRevealDelay = kind === 'card' ? String(Math.min(row.indexOf(element), 2) * 80) : '0';
        // Never fade out content already painted in the initial/restored viewport.
        if (rect.height && rect.top < window.innerHeight) {
          this.#finish(element);
        } else {
          element.dataset.arvanaReveal = 'waiting';
          this.#pending.add(element);
          this.#observer.observe(element);
        }
      }
    }
  }

  #enter = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const element = entry.target;
      this.#observer?.unobserve(element);
      this.#pending.delete(element);
      if (this.#seen.has(element)) continue;
      this.#seen.add(element);
      if (this.#motion.matches || element.contains(document.activeElement)) {
        this.#finish(element);
        continue;
      }
      const rect = element.getBoundingClientRect();
      const mobile = window.innerWidth < 750;
      const distance = mobile ? 16 : 32;
      let direction = 'up';
      // Read the rendered position, so reversed images and RTL layouts need no special cases.
      if (!mobile && element.dataset.arvanaRevealKind === 'side' && rect.width < window.innerWidth * 0.58) {
        const center = rect.left + rect.width / 2;
        if (center < window.innerWidth * 0.42) direction = 'left';
        if (center > window.innerWidth * 0.58) direction = 'right';
      }
      const from = direction === 'left' ? `-${distance}px 0px` :
        direction === 'right' ? `${distance}px 0px` : `0px ${distance}px`;
      element.dataset.arvanaRevealDirection = direction;
      element.dataset.arvanaReveal = 'running';
      try {
        const animation = element.animate([
          { opacity: 0, translate: from }, { opacity: 1, translate: '0px 0px' },
        ], {
          duration: mobile ? 480 : 640,
          delay: Number(element.dataset.arvanaRevealDelay),
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both',
        });
        this.#animations.set(element, animation);
        animation.finished.then(() => this.#finish(element), () => this.#finish(element));
      } catch {
        this.#finish(element);
      }
    }
  };

  #finish(element) {
    this.#seen.add(element);
    this.#observer?.unobserve(element);
    this.#pending.delete(element);
    const animation = this.#animations.get(element);
    this.#animations.delete(element);
    animation?.cancel();
    element.dataset.arvanaReveal = 'done';
  }

  #finishRelated(target) {
    for (const element of [...this.#pending, ...this.#animations.keys()]) {
      if (element.contains(target) || target.contains(element)) this.#finish(element);
    }
  }

  #interaction = (event) => {
    if (event.target instanceof Element) this.#finishRelated(event.target);
  };

  #anchor = () => {
    if (!window.location.hash) return;
    try {
      const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (target) this.#finishRelated(target);
    } catch {
      // A malformed URL fragment must not interrupt storefront enhancement.
    }
  };
}

if (!customElements.get('arvana-scroll-reveal-component')) {
  customElements.define('arvana-scroll-reveal-component', ArvanaScrollReveal);
}
